import { ClipboardList, Split } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Callout, EmptyState, Skeleton } from "@/components/ui/feedback";
import { readbackOf, statusText, type Readback } from "@/features/intake-readback/readback";
import { Locked, StepMark, StepTitle } from "@/features/intake-live/wizard";
import { progressText } from "@/lib/intake-steps";
import { useLeadIntake, type IntakeStep } from "@/lib/queries-intake";

/**
 * What the intake asked and what was answered, read off a call (ACL-477).
 *
 * The answers were always saved and the API always returned them; what was missing was anywhere
 * to look. The in-call panel mounts only while there is a call to be about (`intakePlacement`
 * needs a call id), so once the rep hung up the questions and their answers were reachable only
 * as loose contact fields on the All fields tab — values with no questions, no order, and no sign
 * of what was skipped. That is what "we cannot see what was filled" meant.
 *
 * **Read-only, deliberately.** This draws the same rows as the wizard and offers no input, no
 * skip and no reopen. Changing an answer re-walks the flow and can move the lead onto another
 * branch (ADR-110); that belongs on the call, with the rep who is asking, not in a details pane
 * somebody opened to look something up. So this holds one query and no mutation hook at all.
 *
 * **The same walk the wizard draws.** `walkOf` is the one reading of the route — it decides
 * order, numbering and what counts as done — so a question that reads as third here reads as
 * third on the call. Only the rows differ, and those reuse the wizard's own title and glyph.
 *
 * What to show is `readbackOf`'s, not this component's: which empty state a reader is in is a
 * rule with a test rather than a chain of `&&` in the markup.
 */
export function IntakeReadback({ leadId }: { leadId: string | null }) {
  const { data, isPending, isError } = useLeadIntake(leadId ?? undefined);
  const view = readbackOf(leadId, data);

  if (view.kind === "no-record") {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No contact record"
        description="This conversation is not linked to a contact, so there is no intake to read."
      />
    );
  }
  // Order matters: a reading in flight is not yet "no flow", and an error is not an empty intake.
  if (isPending) return <Loading />;
  if (isError) {
    return (
      <Callout tone="danger">The intake could not be loaded. Refresh the page to try again.</Callout>
    );
  }
  if (view.kind === "no-flow") {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No intake questions"
        description="Nobody has published an intake flow yet, so there is nothing to ask or show."
      />
    );
  }

  return (
    <div className="space-y-3">
      <Version view={view} />

      {view.kind === "nothing-answered" ? (
        <EmptyState
          icon={ClipboardList}
          title="Nothing answered yet"
          description="The questions are asked on the call. Nothing has been recorded for this contact."
        />
      ) : (
        <>
          {view.walk.problem ? (
            <Callout tone="warning">
              This intake cannot be walked from here, so the list may be incomplete.
            </Callout>
          ) : null}

          {view.walk.steps.length > 0 ? (
            <section className="overflow-hidden rounded-lg border border-border">
              <p className="border-b border-border bg-surface-sunken px-3 py-1.5 text-xs text-text-muted">
                {progressText(view.walk)}
              </p>
              <ul className="divide-y divide-border">
                {view.walk.steps.map((one) => (
                  <li key={one.step.key} className="flex items-start gap-2.5 px-3 py-2.5">
                    <StepMark one={one} />
                    <div className="min-w-0 flex-1">
                      {one.decision ? (
                        <Decision step={one.step} />
                      ) : (
                        <>
                          {/* `answered` decides whether the row shows what was said; the question
                              being asked now has no answer to show yet. */}
                          <StepTitle step={one.step} answered={!one.current} />
                          <Unreadable step={one.step} />
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {view.offPath.length > 0 ? <OffPath steps={view.offPath} /> : null}
        </>
      )}
    </div>
  );
}

/**
 * Which version these answers belong to.
 *
 * A run keeps the version it started on, so a lead part-way through an older flow finishes on it
 * even after a newer one is published (ADR-110). `outdated` is the server's own comparison
 * against the live version of the same tree, and saying so is what stops a reader checking these
 * answers against questions the admin has since changed.
 */
function Version({ view }: { view: Readback }) {
  if (!view.version) return null;
  const status = statusText(view.status);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge tone="neutral">
        {view.version.title} v{view.version.version}
      </Badge>
      {status ? (
        <Badge tone={view.status === "completed" ? "success" : "neutral"}>{status}</Badge>
      ) : null}
      {view.outdated ? (
        <span className="text-xs text-text-muted">
          A newer version has been published; these answers belong to the one above.
        </span>
      ) : null}
    </div>
  );
}

/** A question this reader may not see. The engine routes on it either way. */
function Unreadable({ step }: { step: IntakeStep }) {
  if (step.readable) return null;
  return (
    <div className="mt-2">
      <Locked>This question is not yours to see, so neither is the answer.</Locked>
    </div>
  );
}

/** A split the flow settled for itself, drawn where the walk passed it. */
function Decision({ step }: { step: IntakeStep }) {
  return (
    <span className="flex items-center gap-1.5 text-sm text-text-muted">
      <Split className="size-3.5 shrink-0" aria-hidden />
      {step.title}
    </span>
  );
}

/**
 * Answers the route no longer passes through.
 *
 * A driver who turns out to be a pedestrian keeps the answers the driver branch asked: the path
 * is recomputed from current values, so changing one answer moves the route and leaves the rest
 * behind, saved (ADR-110). They are listed apart rather than mixed into the route, because they
 * are not steps of it — but they are listed, because "where did that answer go?" is the question
 * this panel exists to answer.
 */
function OffPath({ steps }: { steps: IntakeStep[] }) {
  return (
    <section className="overflow-hidden rounded-lg border border-border">
      <p className="border-b border-border bg-surface-sunken px-3 py-1.5 text-xs text-text-muted">
        Answered earlier, not on the current route
      </p>
      <ul className="divide-y divide-border">
        {steps.map((step) => (
          <li key={step.key} className="px-3 py-2.5">
            <StepTitle step={step} answered />
            <Unreadable step={step} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function Loading() {
  return (
    <div className="space-y-2" aria-busy>
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}
